import { useState, type DragEvent, type KeyboardEvent } from "react";
import { COUNTRIES, countriesOf, HOSTEL_SLUGS, HOSTELS, nightsOf, TRIPS } from "./content";
import GetTripButton from "./GetTripButton";
import { BUILT_ROUTE_NAME } from "./createTripFromRoute";
import { changeNights, dayRanges, inRoute, km, knownLeg, moveStop, nearest, removeStop, toggleStop, totalNights, type RouteStop } from "./plannerLogic";
import { Arrow, Flag, MM_MONKEY } from "./ui";
import styles from "./Planner.module.scss";

/**
 * The route planner beside the hostel map: the Stippl planner's Route tab (design/screens/PlanFourStops) and its
 * "Add new destination" sheet (PlanAddDestination). The route itself lives in the route store; this draws it
 * and says how it changes. The map next to it draws the same route.
 */

const cx = (...names: (string | false | undefined)[]) => names.filter(Boolean).join(" ");

const HomeIcon = () => (
	<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinejoin="round" aria-hidden="true">
		<path d="M4 10.5 12 4l8 6.5V20H4z" />
		<path d="M9.5 20v-5h5v5" />
	</svg>
);

const Minus = () => (
	<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
		<path d="M5 12h14" stroke="#0f3e4a" strokeWidth="2.4" strokeLinecap="round" />
	</svg>
);

const Plus = ({ size = 18, color = "#0f3e4a", weight = 2.4 }: { size?: number; color?: string; weight?: number }) => (
	<svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
		<path d="M12 5v14M5 12h14" stroke={color} strokeWidth={weight} strokeLinecap="round" />
	</svg>
);

const Pin = () => (
	<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#0f3e4a" strokeWidth="2" aria-hidden="true">
		<path d="M12 21s7-6.2 7-12a7 7 0 1 0-14 0c0 5.8 7 12 7 12z" />
		<circle cx="12" cy="9" r="2.5" />
	</svg>
);

const Search = () => (
	<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#0f3e4a" strokeWidth="2.2" aria-hidden="true">
		<circle cx="11" cy="11" r="6.5" />
		<path d="m16 16 4.5 4.5" strokeLinecap="round" />
	</svg>
);

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

interface PlannerProps {
	route: RouteStop[];
	setRoute: (next: RouteStop[] | ((route: RouteStop[]) => RouteStop[])) => void;
	/** a ready-made route was picked from the empty planner */
	onLoadTrip: (tripId: string) => void;
}

const Planner = ({ route, setRoute, onLoadTrip }: PlannerProps) => {
	const [adding, setAdding] = useState(false);
	const [query, setQuery] = useState("");
	const [dragFrom, setDragFrom] = useState<number | null>(null);
	const [dragOver, setDragOver] = useState<number | null>(null);

	const endDrag = () => {
		setDragFrom(null);
		setDragOver(null);
	};
	const onDrop = (e: DragEvent, to: number) => {
		e.preventDefault();
		if (dragFrom !== null) setRoute((r) => moveStop(r, dragFrom, to));
		endDrag();
	};
	// Alt + arrow moves a stop for people who cannot drag.
	const onRowKey = (e: KeyboardEvent, i: number) => {
		if (!e.altKey || (e.key !== "ArrowUp" && e.key !== "ArrowDown")) return;
		e.preventDefault();
		setRoute((r) => moveStop(r, i, i + (e.key === "ArrowUp" ? -1 : 1)));
	};

	const ccs = countriesOf(route.map((r) => r.s));
	const days = dayRanges(route);
	const last = route[route.length - 1];

	const header = (
		<div className={styles.head}>
			<div>
				<b>{route.length ? BUILT_ROUTE_NAME : "Where to first, legend?"}</b>
				<small>
					{route.length
						? `${plural(route.length, "stop")} · ${ccs.map((c) => COUNTRIES[c]).join(", ")}`
						: `${HOSTEL_SLUGS.length} Mad Monkeys, one route. Yours.`}
				</small>
			</div>
			{route.length > 0 && <span className={styles.nights}>{totalNights(route)} nights planned</span>}
		</div>
	);

	const addSheet = adding && (
		<AddSheet
			route={route}
			query={query}
			onQuery={setQuery}
			onToggle={(slug) => setRoute((r) => toggleStop(r, slug))}
			onClose={() => {
				setAdding(false);
				setQuery("");
			}}
		/>
	);

	const empty = (
		<>
			{header}
			<div className={styles.body}>
				<div className={styles.empty}>
					<b>Start with your first Mad Monkey</b>
					<p>Pick a hostel, then the next one. We draw the route, you bring the crew.</p>
					<button type="button" id="mm-planner-first" className={styles.go} onClick={() => setAdding(true)}>
						<img src={MM_MONKEY} alt="" />
						Pick your first Mad Monkey
					</button>
					<span className={styles.or}>or grab a ready-made route</span>
					<div className={styles.presets}>
						{TRIPS.map((t) => (
							<button type="button" key={t.id} id={`mm-planner-preset-${t.id}`} className={styles.preset} onClick={() => onLoadTrip(t.id)}>
								<span className={styles.presetImg}>
									<img src={HOSTELS[t.stops[0][0]].photos[0]} alt="" loading="lazy" />
									<img className={styles.presetMonkey} src={MM_MONKEY} alt="" />
								</span>
								<span className={styles.presetText}>
									<b>{t.name}</b>
									<small>
										{nightsOf(t)} nights · {t.stops.length} Mad Monkeys
									</small>
									<span className={styles.presetFlags}>
										{countriesOf(t.stops.map(([s]) => s)).map((c) => (
											<Flag key={c} cc={c} size={15} />
										))}
									</span>
								</span>
								<span className={styles.presetArrow}>
									<Arrow />
								</span>
							</button>
						))}
					</div>
				</div>
			</div>
		</>
	);

	const stops = route.length > 0 && (
		<>
			{header}
			<div className={styles.body}>
				<ol className={styles.list}>
					{route.map((r, i) => {
						const h = HOSTELS[r.s];
						const next = route[i + 1];
						const distance = next ? km(r.s, next.s) : 0;
						return (
							<li
								key={r.s}
								className={cx(styles.row, dragFrom === i && styles.drag, dragOver === i && dragFrom !== i && styles.over)}
								draggable
								tabIndex={0}
								aria-label={`${h.name}, stop ${i + 1}, ${plural(r.n, "night")}. Hold Alt and press an arrow key to move it.`}
								onDragStart={(e) => {
									setDragFrom(i);
									e.dataTransfer.effectAllowed = "move";
									e.dataTransfer.setData("text/plain", r.s);
								}}
								onDragOver={(e) => {
									if (dragFrom === null) return;
									e.preventDefault();
									setDragOver(i);
								}}
								onDrop={(e) => onDrop(e, i)}
								onDragEnd={endDrag}
								onKeyDown={(e) => onRowKey(e, i)}
							>
								<span className={cx(styles.ix, i === 0 && styles.home)}>{i ? i + 1 : <HomeIcon />}</span>
								<div className={styles.txt}>
									<b>
										<Flag cc={h.cc} />
										{h.name}
									</b>
									<small>{days[i][0] === days[i][1] ? `Day ${days[i][0]}` : `Day ${days[i][0]} – ${days[i][1]}`} · Mad Monkey</small>
								</div>
								<div className={styles.stepper}>
									<button type="button" onClick={() => setRoute((x) => changeNights(x, i, -1))} aria-label={`One night less in ${h.name}`}>
										<Minus />
									</button>
									<span>
										<b>{r.n}</b>
										<i>{r.n === 1 ? "night" : "nights"}</i>
									</span>
									<button type="button" onClick={() => setRoute((x) => changeNights(x, i, 1))} aria-label={`One night more in ${h.name}`}>
										<Plus />
									</button>
								</div>
								<button type="button" className={styles.remove} onClick={() => setRoute((x) => removeStop(x, i))} aria-label={`Remove ${h.name}`}>
									×
								</button>
								{next && (
									<span className={styles.km} title={knownLeg(TRIPS, r.s, next.s) ?? (distance > 500 ? "Short flight" : "Bus or minivan")}>
										{distance} km
									</span>
								)}
							</li>
						);
					})}
				</ol>
				<div className={styles.add}>
					<div>
						<span className={styles.sugLabel}>Next stop ideas</span>
						<div className={styles.sug}>
							{nearest(route, last.s).map((k) => (
								<button type="button" key={k} className={styles.chip} onClick={() => setRoute((r) => toggleStop(r, k))}>
									+ {HOSTELS[k].name} <small>{km(last.s, k)} km</small>
								</button>
							))}
						</div>
					</div>
				</div>
			</div>
			<div className={cx(styles.foot, styles.big)}>
				<button type="button" id="mm-planner-clear" className={styles.ghost} onClick={() => setRoute([])}>
					Start over
				</button>
				<button type="button" id="mm-planner-add" className={styles.bigPlus} onClick={() => setAdding(true)} aria-label="Add a Mad Monkey">
					<Plus size={30} color="#fff" weight={2.6} />
				</button>
				{/* The third column of the footer grid; "Optimize route" stood here until the 16 Oct review took it out. */}
				<span />
			</div>
		</>
	);

	return (
		<div className={styles.col}>
			<div className={styles.planner} aria-live="polite">
				{addSheet || stops || empty}
			</div>
			{route.length > 0 && !adding && (
				<div className={styles.cta}>
					<GetTripButton id="mm-get-trip-builder" stops={route} name={BUILT_ROUTE_NAME} source="builder" className={styles.getTrip}>
						<img src={MM_MONKEY} alt="" />
						Get this trip
						<Arrow />
					</GetTripButton>
				</div>
			)}
		</div>
	);
};

interface AddSheetProps {
	route: RouteStop[];
	query: string;
	onQuery: (q: string) => void;
	onToggle: (slug: string) => void;
	onClose: () => void;
}

/** Every hostel by country, the country of the last stop first and the nearest to it first; a search narrows them. */
const AddSheet = ({ route, query, onQuery, onToggle, onClose }: AddSheetProps) => {
	const last = route.length ? route[route.length - 1].s : null;
	const q = query.trim().toLowerCase();
	const hits = HOSTEL_SLUGS.filter((s) => !q || HOSTELS[s].name.toLowerCase().includes(q) || COUNTRIES[HOSTELS[s].cc].toLowerCase().includes(q));
	const order = Object.keys(COUNTRIES).sort(
		(a, b) => (last && HOSTELS[last].cc === a ? -1 : 0) - (last && HOSTELS[last].cc === b ? -1 : 0) || COUNTRIES[a].localeCompare(COUNTRIES[b])
	);
	const groups = order
		.map(
			(c) =>
				[
					c,
					hits.filter((s) => HOSTELS[s].cc === c).sort((x, y) => (last ? km(last, x) - km(last, y) : HOSTELS[x].name.localeCompare(HOSTELS[y].name))),
				] as const
		)
		.filter(([, list]) => list.length);

	return (
		<div className={styles.sheet}>
			<div className={styles.sheetTop}>
				<button type="button" className={styles.sheetClose} onClick={onClose} aria-label="Close">
					×
				</button>
				<b>
					<Pin />
					Add a Mad Monkey
				</b>
			</div>
			<label className={styles.search}>
				<Search />
				<input
					id="mm-planner-search"
					type="search"
					placeholder='Enter name E.g. "Hoi An"…'
					value={query}
					onChange={(e) => onQuery(e.target.value)}
					aria-label="Search Mad Monkeys"
					autoFocus
				/>
			</label>
			<div className={styles.sheetList}>
				{groups.length ? (
					groups.map(([c, list]) => (
						<div className={styles.group} key={c}>
							<span className={styles.groupKicker}>{plural(list.length, "Mad Monkey")}</span>
							<h4>
								<Flag cc={c} />
								{COUNTRIES[c]}
							</h4>
							{list.map((s) => (
								<button type="button" key={s} className={cx(styles.sheetRow, inRoute(route, s) && styles.in)} onClick={() => onToggle(s)}>
									<img className={styles.sheetImg} src={HOSTELS[s].photos[0]} alt="" loading="lazy" />
									<span className={styles.sheetText}>
										<b>{HOSTELS[s].name}</b>
										<small>{HOSTELS[s].tag}</small>
									</span>
									<span className={styles.sheetMark}>{inRoute(route, s) ? "✓" : <Plus />}</span>
								</button>
							))}
						</div>
					))
				) : (
					<p className={styles.none}>No Mad Monkey called &quot;{query}&quot; yet.</p>
				)}
			</div>
			<div className={styles.foot}>
				<button type="button" className={styles.go} onClick={onClose}>
					<img src={MM_MONKEY} alt="" />
					Done · {plural(route.length, "stop")}
				</button>
			</div>
		</div>
	);
};

export default Planner;
